import type { ProcessStatus } from "../../api";
import type { StageDraft } from "./types";

/**
 * Which lifecycle state a case is in while it walks each step.
 *
 * The stages and the statuses are one machine -- a case being signed off is in
 * the Approval status -- but the designer used to present them as two unrelated
 * lists, which is where most of the confusion about this engine starts. Drawing
 * the canvas in bands of state says it without a paragraph of prose.
 *
 * The obvious derivation is by stage kind: data entry means Draft, approval
 * means Approval. The outline does exactly that, and it is wrong the moment a
 * process has **data entry after a sign-off** -- a closure record, a hand-back,
 * an implementation report. Those steps run when the case is long past Draft,
 * and labelling them "while in Draft" tells an administrator something false
 * about their own process. It took a genuinely branchy demo process to notice;
 * a straight-line A -> B -> C never exercises it.
 *
 * So the walk is positional, and it follows the lifecycle rows rather than
 * guessing:
 *
 *   * the case starts in the **Draft** state;
 *   * the first approval step moves it into the **Approval** state;
 *   * when a run of approval steps ends, it moves on to wherever the Approval
 *     state's `next_auto_status` points -- or the Active state if nothing
 *     does -- because that transition is what the engine actually performs;
 *   * a later approval step puts it back into the Approval state.
 *
 * Where the lifecycle does not say, this returns `undefined` for that step
 * rather than inventing a band. An unlabelled step is a smaller lie than a
 * wrong label.
 */
export type Phase = {
  /** The status rows this run of steps happens under, if it is knowable. */
  status?: ProcessStatus;
  /** Index of the first stage in the run. */
  from: number;
  /** Index of the last stage in the run, inclusive. */
  to: number;
};

export const statusByKind = (statuses: ProcessStatus[], kind: string) =>
  statuses.find((s) => s.kind === kind);

/** The state each step runs under, one entry per stage, in order. */
export const statusPerStage = (
  stages: StageDraft[],
  statuses: ProcessStatus[]
): Array<ProcessStatus | undefined> => {
  const draft = statusByKind(statuses, "Draft");
  const approval = statusByKind(statuses, "Approval");
  const active = statusByKind(statuses, "Active");
  const byId = new Map(statuses.map((s) => [s.id, s]));

  // Where the engine goes once approvals finish. `next_auto_status` is the
  // template's own answer; the Active state is the fallback for a lifecycle
  // that advances by hand.
  const afterApproval =
    (approval?.next_auto_status != null
      ? byId.get(approval.next_auto_status)
      : undefined) ?? active;

  let current = draft;
  let seenApproval = false;

  return stages.map((stage) => {
    if (stage.kind === "Approval") {
      seenApproval = true;
      current = approval;
      return current;
    }
    // Data entry before anything has been signed is still the draft; data
    // entry *after* a sign-off is the case doing work in whatever state the
    // approval left it in.
    if (seenApproval) current = afterApproval;
    return current;
  });
};

/** The same answer folded into runs, for drawing bands. */
export const phasesOf = (
  stages: StageDraft[],
  statuses: ProcessStatus[]
): Phase[] => {
  const perStage = statusPerStage(stages, statuses);
  const phases: Phase[] = [];
  perStage.forEach((status, index) => {
    const last = phases[phases.length - 1];
    if (last && last.status?.id === status?.id) last.to = index;
    else phases.push({ status, from: index, to: index });
  });
  return phases;
};
